import React, { useState, useEffect } from 'react';
import { fetchChecklists, saveChecklist, deleteChecklist } from '../../utils/api';
import { getNextOccurrence } from '../../utils/scheduleGenerator';
import ChecklistBuilder from './ChecklistBuilder';
import { Button } from '../ui/button';
import { StatusBadge } from '../ui/StatusBadge';
import { Input } from '../ui/input';
import { Spinner } from '../ui/Spinner';

function ChecklistManagement() {
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingChecklist, setEditingChecklist] = useState(null);

  const loadChecklists = async () => {
    setLoading(true);
    try {
      const data = await fetchChecklists();
      setChecklists(data);
    } catch (error) {
      console.error('Error loading checklists:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadChecklists();
  }, []);

  const handleSaveChecklist = async (checklistData) => {
    try {
      await saveChecklist(checklistData);
      await loadChecklists();
      setShowBuilder(false);
      setEditingChecklist(null);
      alert('Checklist saved successfully!');
    } catch (error) {
      console.error('Error saving checklist:', error);
      alert('Error saving checklist');
    }
  };

  const handleEditChecklist = (checklist) => {
    setEditingChecklist(checklist);
    setShowBuilder(true);
  };

  const handleArchiveChecklist = async (id) => {
    if (window.confirm('Are you sure you want to archive this checklist?')) {
      try {
        await deleteChecklist(id);
        await loadChecklists();
        alert('Checklist archived');
      } catch (error) {
        console.error('Error archiving checklist:', error);
        alert('Error archiving checklist');
      }
    }
  };

  const handleCreateNew = () => {
    setEditingChecklist(null);
    setShowBuilder(true);
  };

  const filteredChecklists = checklists
    .filter(c => filter === 'all' || c.status === filter)
    .filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const getFrequencyDisplay = (schedule) => {
    const freq = {
      adhoc: 'Ad-hoc',
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      semiannual: 'Semi-Annual',
      annual: 'Annual'
    };
    return freq[schedule?.frequency] || schedule?.frequency || '';
  };

  if (showBuilder) {
    return (
      <ChecklistBuilder
        existingChecklist={editingChecklist}
        onSave={handleSaveChecklist}
        onCancel={() => {
          setShowBuilder(false);
          setEditingChecklist(null);
        }}
      />
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1 className="dashboard-title">Checklist Management</h1>
        <p className="dashboard-subtitle">Create and manage recurring checklists</p>
      </div>

      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <div style={{ display: 'flex', gap: '8px' }}>
          {['all', 'active', 'draft', 'archived'].map(status => (
            <Button
              key={status}
              onClick={() => setFilter(status)}
              variant={filter === status ? 'primary' : 'default'}
              size="sm"
              style={{ flex: 'none' }}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Button>
          ))}
        </div>

        <Input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search checklists..."
          style={{ flex: 1, maxWidth: 360 }}
        />

        <div className="text-body-sm text-text-muted" style={{ marginLeft: 'auto' }}>
          {filteredChecklists.length} checklist{filteredChecklists.length !== 1 ? 's' : ''}
        </div>

        <Button
          onClick={handleCreateNew}
          variant="primary"
          size="sm"
          style={{ flex: 'none' }}
        >
          + Create New Checklist
        </Button>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 gap-4">
          <Spinner />
          <p className="loading-text">Loading checklists...</p>
        </div>
      ) : filteredChecklists.length === 0 ? (
        <div className="text-center py-16 px-5 text-text-dark-secondary">
          <div className="text-5xl mb-4 opacity-50">✓</div>
          <div className="text-lg font-medium text-text-dark mb-2">No Checklists Found</div>
          <p className="text-body-sm text-text-dark-secondary">
            {searchTerm ? 'Try a different search term' : 'Create your first checklist to get started'}
          </p>
          {!searchTerm && (
            <Button
              onClick={handleCreateNew}
              variant="primary"
              size="sm"
              style={{ marginTop: '16px', flex: 'none' }}
            >
              + Create New Checklist
            </Button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filteredChecklists.map(checklist => {
            const nextOccurrence = checklist.schedule?.frequency !== 'adhoc'
              ? getNextOccurrence(checklist.schedule)
              : null;

            return (
              <div key={checklist.id} className="bg-nn-white rounded-[14px] shadow-card overflow-hidden hover:shadow-md transition-shadow relative">
                <div className="p-5">
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: '600', margin: 0, color: 'var(--text-dark)' }}>{checklist.name}</h3>
                        {checklist.status === 'active'
                          ? <StatusBadge variant="on-track">{(checklist.status || 'active').toUpperCase()}</StatusBadge>
                          : checklist.status === 'draft'
                          ? <StatusBadge variant="watch">{(checklist.status || 'active').toUpperCase()}</StatusBadge>
                          : <StatusBadge variant="info">{(checklist.status || 'active').toUpperCase()}</StatusBadge>
                        }
                      </div>

                      {checklist.description && (
                        <p className="text-body text-text-muted" style={{ margin: '0 0 8px 0' }}>{checklist.description}</p>
                      )}

                      <div className="text-body-sm text-text-muted" style={{ display: 'flex', gap: '16px', marginTop: '8px', flexWrap: 'wrap' }}>
                        <span>{(checklist.questions || []).length} question{(checklist.questions || []).length !== 1 ? 's' : ''}</span>
                        <span>{getFrequencyDisplay(checklist.schedule)}</span>
                        {checklist.schedule?.priority && (
                          <span>{checklist.schedule.priority} priority</span>
                        )}
                        {nextOccurrence && (
                          <span>Next: {nextOccurrence.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', marginLeft: '16px' }}>
                      <Button
                        onClick={() => handleEditChecklist(checklist)}
                        variant="default"
                        size="sm"
                        style={{ flex: 'none' }}
                      >
                        ✎ Edit
                      </Button>
                      <Button
                        onClick={() => handleArchiveChecklist(checklist.id)}
                        variant="danger"
                        size="sm"
                        style={{ flex: 'none' }}
                      >
                        ▢ Archive
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ChecklistManagement;
